import { event as gaEvent } from 'nextjs-google-analytics';

export const trackEvent = (action: string, params?: Record<string, any>) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    gaEvent(action, params);
  }
};

// User interaction events
export const analytics = {
  // Authentication events
  login: (method: string = 'email') => {
    trackEvent('login', { method });
  },

  signup: (method: string = 'email') => {
    trackEvent('sign_up', { method });
  },

  logout: () => {
    trackEvent('logout', {});
  },

  // Profile events
  viewProfile: (userId: string, username: string) => {
    trackEvent('view_profile', {
      user_id: userId,
      username
    });
  },

  followUser: (userId: string, username: string) => {
    trackEvent('follow_user', {
      user_id: userId,
      username
    });
  },

  unfollowUser: (userId: string, username: string) => {
    trackEvent('unfollow_user', {
      user_id: userId,
      username
    });
  },

  // Subscription events
  subscribe: (performerId: string, performerName: string, price: number) => {
    trackEvent('subscribe', {
      performer_id: performerId,
      performer_name: performerName,
      value: price,
      currency: 'EUR'
    });
  },

  unsubscribe: (performerId: string, performerName: string) => {
    trackEvent('unsubscribe', {
      performer_id: performerId,
      performer_name: performerName
    });
  },

  // Content interaction events
  likePost: (postId: string) => {
    trackEvent('like_post', {
      post_id: postId
    });
  },

  unlikePost: (postId: string) => {
    trackEvent('unlike_post', {
      post_id: postId
    });
  },

  commentPost: (postId: string) => {
    trackEvent('comment_post', {
      post_id: postId
    });
  },

  sharePost: (postId: string, method: string = 'link') => {
    trackEvent('share_post', {
      post_id: postId,
      method
    });
  },

  viewPost: (postId: string, contentType: string) => {
    trackEvent('view_post', {
      post_id: postId,
      content_type: contentType
    });
  },

  // Monetization events
  sendTip: (amount: number, performerId: string, performerName: string) => {
    trackEvent('send_tip', {
      value: amount,
      currency: 'EUR',
      performer_id: performerId,
      performer_name: performerName
    });
  },

  purchaseContent: (amount: number, contentId: string, contentType: string) => {
    trackEvent('purchase_content', {
      value: amount,
      currency: 'EUR',
      content_id: contentId,
      content_type: contentType
    });
  },

  addToWallet: (amount: number) => {
    trackEvent('add_to_wallet', {
      value: amount,
      currency: 'EUR'
    });
  },

  // Messaging events
  sendMessage: (conversationType: string) => {
    trackEvent('send_message', {
      conversation_type: conversationType
    });
  },

  // Streaming events
  startStream: () => {
    trackEvent('start_stream', {});
  },

  endStream: (duration: number) => {
    trackEvent('end_stream', {
      duration_seconds: duration
    });
  },

  joinStream: (performerId: string, performerName: string) => {
    trackEvent('join_stream', {
      performer_id: performerId,
      performer_name: performerName
    });
  },

  leaveStream: (performerId: string, performerName: string, duration: number) => {
    trackEvent('leave_stream', {
      performer_id: performerId,
      performer_name: performerName,
      duration_seconds: duration
    });
  },

  // Search events
  search: (searchTerm: string, category?: string) => {
    trackEvent('search', {
      search_term: searchTerm,
      category
    });
  },

  // Navigation events
  clickNavigation: (destination: string) => {
    trackEvent('click_navigation', {
      destination
    });
  },

  // Media events
  uploadMedia: (mediaType: string, count: number = 1) => {
    trackEvent('upload_media', {
      media_type: mediaType,
      count
    });
  },

  // Settings events
  updateProfile: () => {
    trackEvent('update_profile', {});
  },

  updateSettings: (settingType: string) => {
    trackEvent('update_settings', {
      setting_type: settingType
    });
  },

  // Error tracking
  error: (errorType: string, errorMessage: string) => {
    trackEvent('error', {
      error_type: errorType,
      error_message: errorMessage
    });
  },

  // Gallery/Product events
  viewGallery: (galleryId: string, performerId: string) => {
    trackEvent('view_gallery', {
      gallery_id: galleryId,
      performer_id: performerId
    });
  },

  purchaseGallery: (galleryId: string, price: number) => {
    trackEvent('purchase_gallery', {
      gallery_id: galleryId,
      value: price,
      currency: 'EUR'
    });
  },

  viewProduct: (productId: string, performerId: string) => {
    trackEvent('view_product', {
      product_id: productId,
      performer_id: performerId
    });
  },

  purchaseProduct: (productId: string, price: number, quantity: number = 1) => {
    trackEvent('purchase_product', {
      product_id: productId,
      value: price,
      currency: 'EUR',
      quantity
    });
  },

  // Video events
  playVideo: (videoId: string, performerId: string) => {
    trackEvent('play_video', {
      video_id: videoId,
      performer_id: performerId
    });
  },

  pauseVideo: (videoId: string, progress: number) => {
    trackEvent('pause_video', {
      video_id: videoId,
      progress_percent: progress
    });
  },

  completeVideo: (videoId: string, duration: number) => {
    trackEvent('complete_video', {
      video_id: videoId,
      duration_seconds: duration
    });
  },

  purchaseVideo: (videoId: string, price: number) => {
    trackEvent('purchase_video', {
      video_id: videoId,
      value: price,
      currency: 'EUR'
    });
  },

  // Payout events
  requestPayout: (amount: number, method: string) => {
    trackEvent('request_payout', {
      value: amount,
      currency: 'EUR',
      method
    });
  },

  // Notification events
  enableNotifications: (type: string) => {
    trackEvent('enable_notifications', {
      notification_type: type
    });
  },

  disableNotifications: (type: string) => {
    trackEvent('disable_notifications', {
      notification_type: type
    });
  },

  // Feed interaction
  viewFeed: (feedType: string) => {
    trackEvent('view_feed', {
      feed_type: feedType
    });
  },

  createPost: (postType: string, hasMedia: boolean) => {
    trackEvent('create_post', {
      post_type: postType,
      has_media: hasMedia
    });
  },

  deletePost: (postId: string) => {
    trackEvent('delete_post', {
      post_id: postId
    });
  },

  reportContent: (contentId: string, contentType: string, reason: string) => {
    trackEvent('report_content', {
      content_id: contentId,
      content_type: contentType,
      reason
    });
  },

  blockUser: (userId: string) => {
    trackEvent('block_user', {
      user_id: userId
    });
  },

  unblockUser: (userId: string) => {
    trackEvent('unblock_user', {
      user_id: userId
    });
  },

  // Performer specific events
  becomePerformer: () => {
    trackEvent('become_performer', {});
  },

  updateBankingInfo: () => {
    trackEvent('update_banking_info', {});
  },

  updateSubscriptionPrice: (price: number, duration: string) => {
    trackEvent('update_subscription_price', {
      value: price,
      currency: 'EUR',
      duration
    });
  },

  // Referral events
  shareReferralLink: (method: string) => {
    trackEvent('share_referral_link', {
      method
    });
  },

  // Engagement metrics
  timeOnPage: (pagePath: string, seconds: number) => {
    trackEvent('time_on_page', {
      page_path: pagePath,
      duration_seconds: seconds
    });
  },

  scrollDepth: (pagePath: string, depth: number) => {
    trackEvent('scroll_depth', {
      page_path: pagePath,
      depth_percent: depth
    });
  }
};
